// @flow

import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Row, Spinner, Table } from 'reactstrap';
import type { RouterHistory } from 'react-router';
import {
    fetchSupportMetrics,
    formatMetricValue,
    formatMoney,
    formatTimestamp,
    getStatusTone,
    getRiskScope,
    getRiskScopeLabel,
    getRiskScopeTone,
} from './dashboardUtils';

type PropsType = {
    history: RouterHistory,
};

type StateType = {
    loading: boolean,
    error: ?string,
    metrics: any,
    activeDrilldownKey: ?string,
};

class SupportMonitoringDashboard extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            metrics: null,
            activeDrilldownKey: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loading: true, error: null });
        fetchSupportMetrics()
            .then((metrics) => {
                this.setState({ loading: false, metrics, activeDrilldownKey: null });
            })
            .catch((error) => {
                this.setState({ loading: false, error: error.toString() });
            });
    };

    setActiveDrilldown = (drilldownKey: string) => {
        this.setState({ activeDrilldownKey: drilldownKey });
    };

    clearDrilldown = () => {
        this.setState({ activeDrilldownKey: null });
    };

    onSummaryCardKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>, drilldownKey: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setActiveDrilldown(drilldownKey);
        }
    };

    getActiveCard() {
        const { metrics, activeDrilldownKey } = this.state;

        if (!metrics || !activeDrilldownKey) {
            return null;
        }

        return metrics.summaryCards.find((card) => card.drilldownKey === activeDrilldownKey) || null;
    }

    getFollowUpsDue() {
        const { metrics } = this.state;
        const now = Date.now() / 1000;

        return (metrics.requests || []).filter(
            (request) => request.nextFollowUpAt && request.nextFollowUpAt <= now,
        );
    }

    renderSummaryCards() {
        const { metrics, activeDrilldownKey } = this.state;
        return (
            <Row>
                {metrics.summaryCards.map((card) => {
                    const riskScope = getRiskScope(card.drilldownKey);
                    return (
                        <Col md="6" xl="3" key={card.label} className="bottom-spaced-element">
                            <Card
                                className={`dashboard-card dashboard-card-action tone-${card.tone || 'neutral'} ${activeDrilldownKey === card.drilldownKey ? 'is-active' : ''}`}
                                data-testid={`support-summary-card-${card.drilldownKey}`}
                                onClick={() => this.setActiveDrilldown(card.drilldownKey)}
                                onKeyDown={(event) => this.onSummaryCardKeyDown(event, card.drilldownKey)}
                                role="button"
                                tabIndex="0"
                            >
                                <CardBody>
                                    {riskScope ? (
                                        <Badge color={getRiskScopeTone(riskScope)} pill className="mb-2">
                                            {getRiskScopeLabel(riskScope)}
                                        </Badge>
                                    ) : null}
                                    <div className="dashboard-card-label">{card.label}</div>
                                    <div className="dashboard-card-value">{formatMetricValue(card)}</div>
                                    <div className="dashboard-card-help">{card.help}</div>
                                    <div className="dashboard-card-cta">View details</div>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    }

    renderPilotAlertSummary() {
        const { metrics } = this.state;
        const summary = metrics.pilotAlertSummary;
        if (!summary) {
            return null;
        }

        return (
            <Alert color={getStatusTone(summary.severity)} data-testid="support-alert-summary" className="bottom-spaced-element">
                <div className="dashboard-list-title">{summary.title}</div>
                <div>{summary.signal}</div>
                <div className="dashboard-inline-meta">Evidence: {summary.evidence}</div>
                <div className="dashboard-inline-meta">First action: {summary.firstAction}</div>
            </Alert>
        );
    }

    renderRequestQueueContent(requests = this.state.metrics.requests, testId = 'support-queue-table') {
        if (requests.length === 0) {
            return <div className="dashboard-empty-state">No active support requests in the latest sample.</div>;
        }

        return (
            <Table responsive hover className="dashboard-table" data-testid={testId}>
                <thead>
                    <tr>
                        <th>Request</th>
                        <th>Status</th>
                        <th>Assignee</th>
                        <th>Next follow-up</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <tbody>
                    {requests.map((request) => (
                        <tr key={request.id}>
                            <td>{request.name}</td>
                            <td>
                                <Badge color={request.assignee === 'Unassigned' ? 'warning' : 'secondary'}>
                                    {request.status}
                                </Badge>
                            </td>
                            <td>{request.assignee}</td>
                            <td>{formatTimestamp(request.nextFollowUpAt)}</td>
                            <td>{formatTimestamp(request.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    renderRequestQueue(requests = this.state.metrics.requests, testId = 'support-queue-table') {
        return (
            <Card className="dashboard-panel" data-testid="support-queue-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Open support queue</span>
                    <Badge color={getRiskScopeTone('operator')} pill>Operator risk</Badge>
                </CardHeader>
                <CardBody>{this.renderRequestQueueContent(requests, testId)}</CardBody>
            </Card>
        );
    }

    renderTransactionAlertsContent(alerts = this.state.metrics.transactionAlerts, testId = 'support-transaction-alerts-table') {
        const { history } = this.props;
        if (alerts.length === 0) {
            return (
                <div className="dashboard-empty-state">
                    {this.state.metrics.resolvedTransactionHistory.length > 0
                        ? `No active payment exceptions detected. ${this.state.metrics.resolvedTransactionHistory.length} recent exceptions were already recovered or identified as demo history.`
                        : 'No payment exceptions detected in the latest sample.'}
                </div>
            );
        }

        return (
            <Table responsive hover className="dashboard-table" data-testid={testId}>
                <thead>
                    <tr>
                        <th>Transaction</th>
                        <th>Bill</th>
                        <th>Bill status</th>
                        <th>Provider</th>
                        <th>Provider Tx</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <tbody>
                    {alerts.map((transaction) => (
                        <tr
                            key={transaction.id}
                            onClick={() => history.push(`/bill-info/${transaction.billId}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <td>{transaction.id}</td>
                            <td>{transaction.billId}</td>
                            <td>{transaction.billStatus}</td>
                            <td>{transaction.provider || 'payfacto'}</td>
                            <td style={{ maxWidth: 160, wordBreak: 'break-all' }}>
                                {transaction.providerTxId || transaction.merchantNo || '—'}
                            </td>
                            <td><Badge color="danger">{transaction.status}</Badge></td>
                            <td>{formatMoney(transaction.amount)}</td>
                            <td>{formatTimestamp(transaction.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    renderTransactionAlerts(alerts = this.state.metrics.transactionAlerts, testId = 'support-transaction-alerts-table') {
        return (
            <Card className="dashboard-panel" data-testid="support-transaction-alerts-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Payment exception queue</span>
                    <Badge color={getRiskScopeTone('operator')} pill>Operator risk</Badge>
                </CardHeader>
                <CardBody>{this.renderTransactionAlertsContent(alerts, testId)}</CardBody>
            </Card>
        );
    }

    renderResolvedTransactionHistory(alerts = this.state.metrics.resolvedTransactionHistory, testId = 'support-resolved-transaction-history-table') {
        const { history } = this.props;

        return (
            <Card className="dashboard-panel" data-testid="support-resolved-transaction-history-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Resolved or demo payment history</span>
                    <Badge color="secondary" pill>Context only</Badge>
                </CardHeader>
                <CardBody>
                    {alerts.length === 0 ? (
                        <div className="dashboard-empty-state">No resolved or demo payment exceptions were found in the latest sample.</div>
                    ) : (
                        <Table responsive hover className="dashboard-table" data-testid={testId}>
                            <thead>
                                <tr>
                                    <th>Transaction</th>
                                    <th>Bill</th>
                                    <th>State</th>
                                    <th>Provider</th>
                                    <th>Provider Tx</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.map((transaction) => (
                                    <tr
                                        key={transaction.id}
                                        onClick={() => history.push(`/bill-info/${transaction.billId}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{transaction.id}</td>
                                        <td>
                                            <div>{transaction.billId}</div>
                                            <div className="dashboard-inline-meta">{transaction.billStatus}</div>
                                        </td>
                                        <td>
                                            <Badge color={transaction.alertTone || 'secondary'}>{transaction.alertStateLabel}</Badge>
                                            <div className="dashboard-inline-meta">{transaction.resolutionDetail}</div>
                                        </td>
                                        <td>{transaction.provider || 'payfacto'}</td>
                                        <td style={{ maxWidth: 160, wordBreak: 'break-all' }}>
                                            {transaction.providerTxId || transaction.merchantNo || '—'}
                                        </td>
                                        <td>{transaction.status}</td>
                                        <td>{formatMoney(transaction.amount)}</td>
                                        <td>{formatTimestamp(transaction.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderBillAlertsContent(bills = this.state.metrics.billAlerts, testId = 'support-bill-alerts-list') {
        const { history } = this.props;
        if (bills.length === 0) {
            return (
                <div className="dashboard-empty-state">
                    {this.state.metrics.resolvedBillHistory.length > 0
                        ? `No live bills currently require intervention. ${this.state.metrics.resolvedBillHistory.length} older flagged bills were moved into history-only context.`
                        : 'No live bills currently require intervention.'}
                </div>
            );
        }

        return (
            <div className="dashboard-list" data-testid={testId}>
                {bills.map((bill) => (
                    <div
                        className="dashboard-list-row"
                        key={bill.id}
                        onClick={() => history.push(`/bill-info/${bill.id}`)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div>
                            <div className="dashboard-list-title">{bill.locationName}</div>
                            <div className="dashboard-list-meta">
                                {bill.organizationName} · {bill.status} · {formatMoney(bill.total)}
                            </div>
                            <div className="dashboard-inline-meta">
                                Table {bill.tableNumber} · Seat {bill.customerNumber} · {bill.sessionStateLabel}
                            </div>
                            <div className="dashboard-inline-meta">First action: {bill.actionSummary}</div>
                            <div className="dashboard-inline-meta">{bill.resolutionDetail}</div>
                        </div>
                        <div className="dashboard-list-right align-left-mobile">
                            <div className="dashboard-flag-group">
                                <Badge color={bill.alertTone || 'warning'}>{bill.alertStateLabel}</Badge>
                            </div>
                            <div className="dashboard-flag-group">
                                {bill.flags.map((flag) => (
                                    <Badge key={flag} color="warning">{flag}</Badge>
                                ))}
                            </div>
                            <div className="dashboard-list-meta">{formatTimestamp(bill.createdAt)}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderBillAlerts(bills = this.state.metrics.billAlerts, testId = 'support-bill-alerts-list') {
        return (
            <Card className="dashboard-panel" data-testid="support-bill-alerts-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Live bills needing intervention</span>
                    <Badge color={getRiskScopeTone('operator')} pill>Operator risk</Badge>
                </CardHeader>
                <CardBody>{this.renderBillAlertsContent(bills, testId)}</CardBody>
            </Card>
        );
    }

    renderResolvedBillHistory(bills = this.state.metrics.resolvedBillHistory, testId = 'support-resolved-bill-history-list') {
        const { history } = this.props;

        return (
            <Card className="dashboard-panel" data-testid="support-resolved-bill-history-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Old or stale flagged bill history</span>
                    <Badge color="secondary" pill>Context only</Badge>
                </CardHeader>
                <CardBody>
                    {bills.length === 0 ? (
                        <div className="dashboard-empty-state">No old flagged bills were found in the latest sample.</div>
                    ) : (
                        <div className="dashboard-list" data-testid={testId}>
                            {bills.map((bill) => (
                                <div
                                    className="dashboard-list-row"
                                    key={bill.id}
                                    onClick={() => history.push(`/bill-info/${bill.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div>
                                        <div className="dashboard-list-title">{bill.locationName}</div>
                                        <div className="dashboard-list-meta">
                                            {bill.organizationName} · {bill.status} · {formatMoney(bill.total)}
                                        </div>
                                        <div className="dashboard-inline-meta">
                                            Table {bill.tableNumber} · Seat {bill.customerNumber} · {bill.sessionStateLabel}
                                        </div>
                                        <div className="dashboard-inline-meta">First action: {bill.actionSummary}</div>
                                        <div className="dashboard-inline-meta">{bill.resolutionDetail}</div>
                                    </div>
                                    <div className="dashboard-list-right align-left-mobile">
                                        <div className="dashboard-flag-group">
                                            <Badge color={bill.alertTone || 'secondary'}>{bill.alertStateLabel}</Badge>
                                        </div>
                                        <div className="dashboard-flag-group">
                                            {bill.flags.map((flag) => (
                                                <Badge key={flag} color="secondary">{flag}</Badge>
                                            ))}
                                        </div>
                                        <div className="dashboard-list-meta">{formatTimestamp(bill.createdAt)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderGuestWatchlistContent(events = this.state.metrics.guestWatchlist, testId = 'support-guest-watchlist-table') {
        const { history } = this.props;
        if (events.length === 0) {
            return <div className="dashboard-empty-state">No guest distress signals need triage in the latest App Clip sample.</div>;
        }

        return (
            <Table responsive hover className="dashboard-table" data-testid={testId}>
                <thead>
                    <tr>
                        <th>Signal</th>
                        <th>Location</th>
                        <th>Detail</th>
                        <th>Severity</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map((event) => (
                        <tr
                            key={`${event.eventName}-${event.createdAt}-${event.deviceId || 'unknown'}`}
                            onClick={() => {
                                if (event.billId) {
                                    history.push(`/bill-info/${event.billId}`);
                                } else if (event.locationId) {
                                    history.push(`/org_location-info/${event.locationId}`);
                                }
                            }}
                            style={{ cursor: event.billId || event.locationId ? 'pointer' : 'default' }}
                        >
                            <td>{event.title}</td>
                            <td>
                                <div>{event.locationName}</div>
                                <div className="dashboard-inline-meta">{event.organizationName}</div>
                            </td>
                            <td>{event.detail}</td>
                            <td><Badge color={getStatusTone(event.severity)}>{event.severity}</Badge></td>
                            <td>{formatTimestamp(event.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    renderGuestWatchlist(events = this.state.metrics.guestWatchlist, testId = 'support-guest-watchlist-table') {
        return (
            <Card className="dashboard-panel" data-testid="support-guest-watchlist-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Guest distress watchlist</span>
                    <Badge color={getRiskScopeTone('guest')} pill>Guest risk</Badge>
                </CardHeader>
                <CardBody>{this.renderGuestWatchlistContent(events, testId)}</CardBody>
            </Card>
        );
    }

    renderDrilldownPanel() {
        const { metrics, activeDrilldownKey } = this.state;
        const activeCard = this.getActiveCard();

        let content = <div className="dashboard-empty-state">Choose a summary card above to reveal the matching support items.</div>;

        if (activeDrilldownKey === 'openSupportQueue') {
            content = this.renderRequestQueueContent(metrics.drilldowns.openSupportQueue, 'support-drilldown-table-openSupportQueue');
        } else if (activeDrilldownKey === 'followUpsDue') {
            content = this.renderRequestQueueContent(this.getFollowUpsDue(), 'support-drilldown-table-followUpsDue');
        } else if (activeDrilldownKey === 'paymentFailuresToTriage') {
            content = this.renderTransactionAlertsContent(
                metrics.drilldowns.paymentFailuresToTriage,
                'support-drilldown-table-paymentFailuresToTriage',
            );
        } else if (activeDrilldownKey === 'billsNeedingIntervention') {
            content = this.renderBillAlertsContent(
                metrics.drilldowns.billsNeedingIntervention,
                'support-drilldown-list-billsNeedingIntervention',
            );
        } else if (activeDrilldownKey === 'guestDistressWatchlist') {
            content = this.renderGuestWatchlistContent(
                metrics.drilldowns.guestDistressWatchlist,
                'support-drilldown-table-guestDistressWatchlist',
            );
        }

        return (
            <Card className="dashboard-panel bottom-spaced-element" data-testid="support-drilldown-panel">
                <CardHeader className="dashboard-panel-header dashboard-panel-header-with-action">
                    <div>
                        <div>{activeCard ? `${activeCard.label} details` : 'Metric drill-down'}</div>
                        <div className="dashboard-panel-subtitle">
                            {activeCard ? activeCard.help : 'Select a summary card to reveal the underlying support records.'}
                        </div>
                    </div>
                    {activeCard ? (
                        <Button color="secondary" outline size="sm" onClick={this.clearDrilldown}>
                            Clear
                        </Button>
                    ) : null}
                </CardHeader>
                <CardBody>{content}</CardBody>
            </Card>
        );
    }

    render() {
        const { loading, error, metrics } = this.state;
        const { history } = this.props;

        return (
            <div className="operations-page" data-testid="support-monitor-page">
                <div className="operations-hero compact d-flex justify-content-between align-items-start flex-wrap">
                    <div>
                        <div className="operations-kicker">Support and monitoring</div>
                        <h1 className="operations-title">Support hub</h1>
                        <p className="operations-subtitle">
                            Triage restaurant and guest issues: payment failures, live bill intervention, and distress signals.
                            Payment ledgers live under Operations overview.
                        </p>
                    </div>
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        className="mt-2"
                        onClick={() => history.push('/operations-dashboard')}
                        data-testid="support-open-payment-ledger"
                    >
                        Payment ledger →
                    </Button>
                </div>
                {loading ? <div className="dashboard-loading-state"><Spinner color="primary" /></div> : null}
                {error ? <Alert color="danger">{error}</Alert> : null}
                {!loading && !error && metrics ? (
                    <div>
                        {this.renderPilotAlertSummary()}
                        {this.renderSummaryCards()}
                        {this.renderDrilldownPanel()}
                        <Row>
                            <Col xl="7" className="bottom-spaced-element">{this.renderGuestWatchlist()}</Col>
                            <Col xl="5" className="bottom-spaced-element">{this.renderBillAlerts()}</Col>
                        </Row>
                        <Row>
                            <Col xl="12" className="bottom-spaced-element">{this.renderResolvedBillHistory()}</Col>
                        </Row>
                        <Row>
                            <Col xl="5" className="bottom-spaced-element">{this.renderRequestQueue()}</Col>
                            <Col xl="7" className="bottom-spaced-element">{this.renderTransactionAlerts()}</Col>
                        </Row>
                        <Row>
                            <Col xl="12" className="bottom-spaced-element">{this.renderResolvedTransactionHistory()}</Col>
                        </Row>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default withRouter(SupportMonitoringDashboard);
